import { useEffect, useRef, useState, type MouseEvent, type KeyboardEvent } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { LogEntry as LogEntryType, MuteRuleKind } from '../types/index';

// Enable plugins for timezone conversion and relative time
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

interface LogEntryProps {
    log: LogEntryType;
    expanded?: boolean;
    onToggleExpand?: () => void;
    onMute?: (kind: MuteRuleKind) => void;
}

function LogEntry({ log, expanded: expandedProp, onToggleExpand, onMute }: LogEntryProps) {
    // Use prop-based expanded state if provided, otherwise fall back to local state
    const [localExpanded, setLocalExpanded] = useState(false);
    const expanded = expandedProp !== undefined ? expandedProp : localExpanded;
    const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
    const [muteMenuOpen, setMuteMenuOpen] = useState(false);
    const muteMenuContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!muteMenuOpen) return;

        const handleMouseDown = (event: globalThis.MouseEvent) => {
            const target = event.target as Node | null;
            if (!target) return;

            if (muteMenuContainerRef.current && !muteMenuContainerRef.current.contains(target)) {
                setMuteMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [muteMenuOpen]);

    const parseJsonSafely = (data: string): unknown => {
        try {
            return JSON.parse(data);
        } catch {
            return data;
        }
    };

    const parseEmbeddedJson = (text: string): string => {
        // Look for JSON strings embedded in text and parse them
        const jsonRegex = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;

        return text.replace(jsonRegex, (match) => {
            try {
                const parsed = JSON.parse(match);
                return JSON.stringify(parsed, null, 2);
            } catch {
                return match;
            }
        });
    };

    const deepParseJsonStrings = (obj: unknown): unknown => {
        if (typeof obj === 'string') {
            // Try to parse the entire string as JSON first
            try {
                const parsed = JSON.parse(obj);
                return deepParseJsonStrings(parsed);
            } catch {
                // If that fails, look for embedded JSON and format it
                return parseEmbeddedJson(obj);
            }
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => deepParseJsonStrings(item));
        }

        if (obj && typeof obj === 'object') {
            const result: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(obj)) {
                result[key] = deepParseJsonStrings(value);
            }
            return result;
        }

        return obj;
    };

    const copyLogToClipboard = async () => {
        try {
            const logData: Record<string, unknown> = {
                timestamp: log.timestamp,
                level: log.level,
                project: log.project,
                message: deepParseJsonStrings(log.message)
            };

            if (log.service) logData.service = log.service;
            if (log.category) logData.category = log.category;
            if (log.env) logData.env = log.env;
            if (log.host) logData.host = log.host;
            if (log.severity !== null && log.severity !== undefined) logData.severity = log.severity;
            if (log.tags && log.tags.length > 0) logData.tags = log.tags;

            if (log.error && Object.keys(log.error).length > 0) {
                logData.error = deepParseJsonStrings(log.error);
            }

            if (log.context && Object.keys(log.context).length > 0) {
                logData.context = deepParseJsonStrings(log.context);
            }

            if (log.extra_data && log.extra_data.trim().length > 0) {
                const parsedExtraData = parseJsonSafely(log.extra_data);
                logData.extra_data = deepParseJsonStrings(parsedExtraData);
            }

            if (log.ingest_time) logData.ingest_time = log.ingest_time;

            const formattedData = JSON.stringify(logData, null, 2);
            await navigator.clipboard.writeText(formattedData);

            setCopyFeedback('Copied!');
            setTimeout(() => setCopyFeedback(null), 2000);
        } catch {
            setCopyFeedback('Copy failed');
            setTimeout(() => setCopyFeedback(null), 2000);
        }
    };

    const getLevelIcon = (level: string) => {
        switch (level.toLowerCase()) {
            case 'fatal':
                return 'fas fa-skull text-red-600';
            case 'error':
                return 'fas fa-exclamation-circle text-red-500';
            case 'warn':
                return 'fas fa-exclamation-triangle text-yellow-500';
            case 'info':
                return 'fas fa-info-circle text-blue-500';
            case 'debug':
                return 'fas fa-bug text-gray-500';
            case 'trace':
                return 'fas fa-search text-gray-600';
            case 'job':
                return 'fas fa-briefcase text-green-500';
            default:
                return 'fas fa-circle text-gray-500';
        }
    };

    const getLevelClass = (level: string) => {
        switch (level.toLowerCase()) {
            case 'fatal':
                return 'log-level-fatal';
            case 'error':
                return 'log-level-error';
            case 'warn':
                return 'log-level-warn';
            case 'info':
                return 'log-level-info';
            case 'debug':
                return 'log-level-debug';
            case 'trace':
                return 'log-level-trace';
            case 'job':
                return 'log-level-job';
            default:
                return '';
        }
    };

    const toggleExpanded = () => {
        if (onToggleExpand) {
            onToggleExpand();
        } else {
            setLocalExpanded(!localExpanded);
        }
    };

    const formatTimestamp = (timestamp: string, isUTC: boolean = false) => {
        // Convert UTC to EST if needed
        const date = isUTC ? dayjs.utc(timestamp).tz('America/New_York') : dayjs(timestamp);

        const now = dayjs();
        const diffInSeconds = now.diff(date, 'second');
        const diffInHours = now.diff(date, 'hour');

        // If it's in the future or within the last minute, show "Now"
        if (diffInSeconds < 60) {
            return `Now`;
        }

        // If it's from today, show relative time and time
        if (diffInHours < 24 && date.isSame(now, 'day')) {
            return `${date.fromNow()} at ${date.format('h:mm A')}`;
        }

        // If it's from this week, show day and time
        if (diffInHours < 168) {
            // 7 days
            return date.format('ddd h:mm A');
        }

        // If it's older, show date and time in a readable format
        return date.format('MMM D, YYYY at h:mm A');
    };

    const hasAdditionalData = () => {
        return (
            (log.error && Object.keys(log.error).length > 0) ||
            (log.context && Object.keys(log.context).length > 0) ||
            (log.extra_data && log.extra_data.trim().length > 0) ||
            (log.tags && log.tags.length > 0)
        );
    };

    // Get project-specific styling for badges
    const getProjectBadgeClasses = (project: string) => {
        const projectLower = project.toLowerCase();

        if (projectLower === 'project314') {
            return 'bg-sky-500 text-white';
        }

        if (projectLower.startsWith('watchmode')) {
            return 'bg-emerald-500 text-white';
        }

        if (projectLower.startsWith('brain')) {
            return 'bg-purple-500 text-white';
        }

        // Fallback to a neutral color for unknown projects
        return 'bg-zinc-600 text-white';
    };

    // Get log level badge styling
    const getLevelBadgeClasses = (level: string) => {
        switch (level.toLowerCase()) {
            case 'fatal':
                return 'bg-red-600 text-white';
            case 'error':
                return 'bg-red-500 text-white';
            case 'warn':
                return 'bg-yellow-500 text-black';
            case 'info':
                return 'bg-blue-500 text-white';
            case 'debug':
                return 'bg-gray-500 text-white';
            case 'trace':
                return 'bg-gray-600 text-white';
            case 'job':
                return 'bg-green-500 text-white';
            default:
                return 'bg-zinc-500 text-white';
        }
    };

    // Get severity badge styling with better contrast
    const getSeverityBadgeClasses = () => {
        return 'bg-orange-500 text-white';
    };

    // Format error object for better display
    const formatError = (error: Record<string, unknown>) => {
        const { name, message, stack, ...otherProps } = error;

        return {
            name: name as string,
            message: message as string,
            stack: stack as string,
            otherProps: Object.keys(otherProps).length > 0 ? otherProps : null
        };
    };

    // Format stack trace for better readability
    const formatStackTrace = (stack: string) => {
        if (!stack) return null;

        return stack
            .split('\n')
            .map((line, index) => {
                // First line is usually the error message, skip it since we show it separately
                if (index === 0 && line.includes(':')) return null;

                const trimmed = line.trim();
                if (!trimmed) return null;

                // Highlight file paths and line numbers
                if (trimmed.startsWith('at ')) {
                    const parts = trimmed.replace('at ', '').split(' ');
                    const functionName = parts[0];
                    const location = parts[1]?.replace(/[()]/g, '');

                    return (
                        <div key={index} className="text-red-300 text-xs">
                            <span className="text-red-100 font-medium">at </span>
                            <span className="text-red-200">{functionName}</span>
                            {location && <span className="text-red-400 ml-2">({location})</span>}
                        </div>
                    );
                }

                return (
                    <div key={index} className="text-red-300 text-xs">
                        {trimmed}
                    </div>
                );
            })
            .filter(Boolean);
    };

    // Handle clicking on the entire log row
    const handleRowClick = (e: MouseEvent) => {
        // Don't expand if clicking on certain interactive elements
        const target = e.target as HTMLElement;
        if (target.tagName === 'BUTTON' || target.closest('button') || target.tagName === 'A' || target.closest('a')) {
            return;
        }

        // Don't close if clicking within the content sections (to allow text selection)
        if (
            expanded &&
            (target.closest('.bg-red-950') || // Error details section
                target.closest('.bg-blue-950') || // Context section
                target.closest('.bg-yellow-950')) // Extra data section
        ) {
            return;
        }

        // Toggle expansion
        if (hasAdditionalData()) {
            toggleExpanded();
        }
    };

    // Handle keyboard events on the log row
    const handleRowKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (hasAdditionalData()) {
                toggleExpanded();
            }
        }
    };

    return (
        <div
            className={`log-entry p-4 ${getLevelClass(log.level)} ${hasAdditionalData() ? 'cursor-pointer hover:bg-zinc-800/50' : ''}`}
            onClick={handleRowClick}
            onKeyDown={handleRowKeyDown}
            role={hasAdditionalData() ? 'button' : undefined}
            tabIndex={hasAdditionalData() ? 0 : undefined}
        >
            <div className="flex items-start space-x-3">
                {/* Level Icon */}
                <div className="flex-shrink-0 mt-1">
                    <i className={getLevelIcon(log.level)} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            {/* Timestamp and Meta Info */}
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                                <span className="log-timestamp">
                                    {formatTimestamp(log.ingest_time || log.timestamp, !!log.ingest_time)}
                                </span>

                                <span
                                    className={`px-2 py-1 rounded text-xs font-semibold uppercase tracking-wide ${getLevelBadgeClasses(log.level)}`}
                                >
                                    {log.level}
                                </span>

                                {log.severity !== null && log.severity !== undefined && (
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityBadgeClasses()}`}>
                                        {log.severity}
                                    </span>
                                )}

                                <span
                                    className={`log-project px-2 py-1 rounded text-xs font-medium ${getProjectBadgeClasses(log.project)}`}
                                >
                                    {log.project}
                                </span>

                                {log.service && <span className="bg-zinc-700 px-2 py-1 rounded text-xs">📦 {log.service}</span>}

                                {log.category && <span className="bg-zinc-700 px-2 py-1 rounded text-xs">🏷️ {log.category}</span>}

                                {log.env && <span className="bg-zinc-700 px-2 py-1 rounded text-xs">🌍 {log.env}</span>}

                                {log.host && <span className="bg-zinc-700 px-2 py-1 rounded text-xs">🖥️ {log.host}</span>}
                            </div>

                            {/* Tags */}
                            {log.tags && log.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                    {log.tags.map((tag, index) => (
                                        <span key={index} className="bg-blue-600 text-blue-100 px-2 py-1 rounded-full text-xs">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Message */}
                            <div className="log-message mb-2">{log.message}</div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-2">
                            {/* Mute Button */}
                            {onMute && (
                                <div ref={muteMenuContainerRef} className="relative">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMuteMenuOpen(!muteMenuOpen);
                                        }}
                                        className="flex-shrink-0 text-zinc-400 hover:text-white transition-colors"
                                        aria-label="Mute log entry"
                                        title="Mute (hide from display)"
                                        type="button"
                                    >
                                        <i className="fas fa-eye-slash" />
                                    </button>

                                    {muteMenuOpen && (
                                        <div className="absolute right-0 mt-2 w-64 bg-zinc-900 border border-zinc-700 rounded shadow-lg z-50 overflow-hidden">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onMute('message_exact');
                                                    setMuteMenuOpen(false);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors flex items-center gap-2"
                                                type="button"
                                            >
                                                <i className="fas fa-filter text-zinc-400" />
                                                Mute exact message
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onMute('message_normalized');
                                                    setMuteMenuOpen(false);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors flex items-center gap-2"
                                                type="button"
                                            >
                                                <i className="fas fa-compress-arrows-alt text-zinc-400" />
                                                Mute similar (normalize ids)
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!log.service) return;
                                                    onMute('service');
                                                    setMuteMenuOpen(false);
                                                }}
                                                className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                                                    log.service ? 'text-zinc-200 hover:bg-zinc-800' : 'text-zinc-500 cursor-not-allowed'
                                                }`}
                                                disabled={!log.service}
                                                type="button"
                                            >
                                                <i className="fas fa-cogs text-zinc-400" />
                                                Mute service
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Copy Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyLogToClipboard();
                                }}
                                className="flex-shrink-0 text-zinc-400 hover:text-white transition-colors relative"
                                aria-label="Copy log entry"
                                title="Copy log entry"
                                type="button"
                            >
                                <i className="fas fa-copy" />
                                {copyFeedback && (
                                    <span className="absolute -top-8 -right-2 bg-zinc-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                        {copyFeedback}
                                    </span>
                                )}
                            </button>

                            {/* Expand Button - Still show for visual indication but make optional */}
                            {hasAdditionalData() && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent the row click handler
                                        toggleExpanded();
                                    }}
                                    className="flex-shrink-0 text-zinc-400 hover:text-white transition-colors"
                                    aria-label={expanded ? 'Collapse details' : 'Expand details'}
                                    type="button"
                                >
                                    <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Expanded Details */}
                    {hasAdditionalData() && expanded && (
                        <div className="expanded-details mt-3 pt-3 border-t border-zinc-700 space-y-3">
                            {/* Error Information */}
                            {log.error &&
                                Object.keys(log.error).length > 0 &&
                                (() => {
                                    const formattedError = formatError(log.error);
                                    return (
                                        <div>
                                            <h4 className="text-sm font-medium text-red-400 mb-3">
                                                <i className="fas fa-exclamation-triangle mr-1" />
                                                Error Details
                                            </h4>
                                            <div className="bg-red-950 border border-red-800 rounded-lg p-4 space-y-3">
                                                {/* Error Type and Message */}
                                                <div className="space-y-2">
                                                    {formattedError.name && (
                                                        <div className="flex items-center space-x-2">
                                                            <span className="text-xs font-medium text-red-300 uppercase tracking-wide">
                                                                Type:
                                                            </span>
                                                            <span className="bg-red-800 px-2 py-1 rounded text-xs font-medium text-red-100">
                                                                {formattedError.name}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {formattedError.message && (
                                                        <div>
                                                            <span className="text-xs font-medium text-red-300 uppercase tracking-wide block mb-1">
                                                                Message:
                                                            </span>
                                                            <div className="bg-red-900 px-3 py-2 rounded border border-red-700">
                                                                <span className="text-red-100 text-sm">{formattedError.message}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Stack Trace */}
                                                {formattedError.stack && (
                                                    <div>
                                                        <span className="text-xs font-medium text-red-300 uppercase tracking-wide block mb-2">
                                                            Stack Trace:
                                                        </span>
                                                        <div className="bg-red-900 px-3 py-2 rounded border border-red-700 font-mono space-y-1 max-h-48 overflow-y-auto">
                                                            {formatStackTrace(formattedError.stack)}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Other Properties */}
                                                {formattedError.otherProps && (
                                                    <div>
                                                        <span className="text-xs font-medium text-red-300 uppercase tracking-wide block mb-2">
                                                            Additional Properties:
                                                        </span>
                                                        <div className="bg-red-900 px-3 py-2 rounded border border-red-700 font-mono text-xs">
                                                            <pre className="text-red-200 whitespace-pre-wrap">
                                                                {JSON.stringify(formattedError.otherProps, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}

                            {/* Context Information */}
                            {log.context && Object.keys(log.context).length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-blue-400 mb-2">
                                        <i className="fas fa-info-circle mr-1" />
                                        Context
                                    </h4>
                                    <div className="bg-blue-950 border border-blue-800 rounded p-3 font-mono text-sm">
                                        <pre className="text-blue-200 whitespace-pre-wrap">{JSON.stringify(log.context, null, 2)}</pre>
                                    </div>
                                </div>
                            )}

                            {/* Extra Data */}
                            {log.extra_data && log.extra_data.trim().length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-yellow-400 mb-2">
                                        <i className="fas fa-database mr-1" />
                                        Extra Data
                                    </h4>
                                    <div className="bg-yellow-950 border border-yellow-800 rounded p-3 font-mono text-sm">
                                        <pre className="text-yellow-200 whitespace-pre-wrap">{log.extra_data}</pre>
                                    </div>
                                </div>
                            )}

                            {/* Ingestion Timestamp */}
                            <div className="text-xs text-zinc-500 space-y-1">
                                <div>
                                    <i className="fas fa-calendar mr-1" />
                                    Event time: {formatTimestamp(log.timestamp)} (original)
                                </div>
                                {log.ingest_time && (
                                    <div>
                                        <i className="fas fa-clock mr-1" />
                                        Ingested: {formatTimestamp(log.ingest_time, true)} EST
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default LogEntry;
